/* House of Ovelle — product page enhancements
   - Subscribe & Save purchase options (selling-plan aware)
   - Size ladder / kit card decoration for variant pills
   - "Complete the ritual" add-ons added in the same click
   - Sticky add-to-bag bar (mirrors the real button label, incl. Pre-order)

   Fix (15 Sep 2026): the purchase-option prices never followed the size
   picker. Dawn's pubsub.js declares PUB_SUB_EVENTS with `const`, which makes a
   global binding but not a window property, so every
   `window.subscribe && window.PUB_SUB_EVENTS` guard below was false — this file
   runs before ovelle-oil-prompt.js, the script that used to copy it onto
   window. It is mirrored here now, and the purchase options also follow the
   form's variant input directly, so they no longer depend on pubsub at all.
*/
(function () {
  const fmt = (cents, format) => {
    if (window.ovelleFormatMoney) return window.ovelleFormatMoney(cents, format);
    const f = format || (window.ovelleMoneyFormat || '${{amount}}');
    const amount = (cents / 100).toFixed(2);
    const noDec = (cents / 100).toFixed(0);
    return f.replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDec)
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/<[^>]+>/g, '');
  };

  if (!window.PUB_SUB_EVENTS && typeof PUB_SUB_EVENTS !== 'undefined') window.PUB_SUB_EVENTS = PUB_SUB_EVENTS;

  /* ---------------- Purchase options ---------------- */
    // Subscription apps (e.g. Appstle) often give every frequency the same plan name ("Monthly Subscription").
  // Derive a human label from the plan's option value ("2MONTH…", "45DAY…") whenever names repeat.
  function frequencyLabel(p) {
    const m = /^(\d+)\s*(DAY|WEEK|MONTH|YEAR)/i.exec(p.opt || '') || /every\s+(\d+)\s*(day|week|month|year)/i.exec(p.desc || '');
    if (!m) return null;
    const n = +m[1], unit = m[2].toLowerCase();
    return n === 1 ? `Every ${unit}` : `Every ${n} ${unit}s`;
  }
  function labelPlans(plans) {
    const names = plans.map((p) => (p.name || '').trim().toLowerCase());
    const dupes = names.some((n, i) => names.indexOf(n) !== i);
    const generic = names.every((n) => /subscri|monthly/.test(n));
    return plans.map((p) => ({ ...p, label: ((dupes || generic) && frequencyLabel(p)) || p.name }));
  }
class OvellePurchaseOptions extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.form = document.getElementById(this.dataset.form);
      this.hasPlans = this.dataset.hasPlans === 'true';
      const raw = this.querySelector('[data-ov-allocations]');
      this.allocations = raw ? JSON.parse(raw.textContent) : {};
      this.radios = this.querySelectorAll('input[type=radio]');
      this.freq = this.querySelector('[data-ov-frequency]');
      this.radios.forEach((r) => r.addEventListener('change', () => this.update()));
      if (this.freq) this.freq.addEventListener('change', () => this.update());
      this.ensureInput();
      this.currentVariant = this.form ? this.form.querySelector('input[name="id"]')?.value : null;
      this.renderVariant(this.currentVariant);
      // Two signals for the same thing; whichever lands first renders, the other is a no-op.
      const follow = (id) => {
        if (!id || String(id) === String(this.currentVariant)) return;
        this.currentVariant = String(id);
        this.renderVariant(this.currentVariant);
      };
      // Dawn writes the chosen variant into the form's id input and fires `change` on it.
      if (this.form) {
        this.onIdChange = (e) => { if (e.target && e.target.name === 'id') follow(e.target.value); };
        this.form.addEventListener('change', this.onIdChange);
      }
      if (window.subscribe && window.PUB_SUB_EVENTS) {
        this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, (e) => {
          if (!e || !e.data || e.data.sectionId !== this.sectionId || !e.data.variant) return;
          // Dawn has re-rendered the buy button by now, so re-apply the plan price to its label.
          if (String(e.data.variant.id) === String(this.currentVariant)) this.update();
          else follow(e.data.variant.id);
        });
      }
    }
    disconnectedCallback() {
      if (this.unsub) this.unsub();
      if (this.form && this.onIdChange) this.form.removeEventListener('change', this.onIdChange);
    }
    ensureInput() {
      if (!this.form) return;
      this.input = this.form.querySelector('input[name="selling_plan"]');
      if (!this.input) {
        this.input = document.createElement('input');
        this.input.type = 'hidden'; this.input.name = 'selling_plan'; this.input.value = '';
        this.form.appendChild(this.input);
      }
    }
    renderVariant(id) {
      const data = this.allocations[id];
      const onetime = this.querySelector('[data-ov-onetime-price]');
      if (data && onetime) onetime.textContent = fmt(data.price);
      if (this.freq && data) {
        const prev = this.freq.value;
        const plans = labelPlans(data.plans);
        this.freq.innerHTML = plans.map((p) => `<option value="${p.id}" data-price="${p.price}" data-compare="${p.compare}">${p.label}</option>`).join('');
        if (prev && [...this.freq.options].some((o) => o.value === prev)) this.freq.value = prev;
        const sub = this.querySelector('[data-ov-option="subscribe"]');
        if (sub) sub.classList.toggle('is-disabled', data.plans.length === 0);
        const subRadio = sub && sub.querySelector('input');
        if (subRadio) subRadio.disabled = data.plans.length === 0;
      }
      this.update();
    }
    selectedPlan() {
      const sub = this.querySelector('input[value="subscribe"]');
      if (!sub || !sub.checked || !this.freq) return null;
      const opt = this.freq.selectedOptions[0];
      return opt ? { id: opt.value, price: +opt.dataset.price, compare: +opt.dataset.compare } : null;
    }
    update() {
      const plan = this.selectedPlan();
      this.querySelectorAll('.ov-purchase__option').forEach((o) => o.classList.toggle('is-selected', o.querySelector('input').checked));
      if (this.input) this.input.value = plan ? plan.id : '';
      const price = this.querySelector('[data-ov-sub-price]');
      const compare = this.querySelector('[data-ov-sub-compare]');
      if (plan) {
        if (price) price.textContent = fmt(plan.price);
        if (compare) compare.textContent = fmt(plan.compare);
      } else if (this.freq && this.freq.options.length) {
        const o = this.freq.options[this.freq.selectedIndex] || this.freq.options[0];
        if (price) price.textContent = fmt(+o.dataset.price);
        if (compare) compare.textContent = fmt(+o.dataset.compare);
      }
      // Reflect in the main price + submit button label
      const btn = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (btn) {
        const span = btn.querySelector('span');
        if (span && !btn.hasAttribute('disabled')) {
          if (!this._label) this._label = btn.dataset.ovStickyLabel || span.textContent.trim();
          span.textContent = plan ? `${this._label} · ${fmt(plan.price)}` : this._label;
        }
      }
      document.dispatchEvent(new CustomEvent('ovelle:purchase-change', { detail: { plan } }));
    }
  }
  if (!customElements.get('ovelle-purchase-options')) customElements.define('ovelle-purchase-options', OvellePurchaseOptions);

  /* ---------------- Variant pill decoration ---------------- */
  // Fix (15 Sep 2026, 1.5.9): Dawn swaps in a fresh <variant-selects> on every
  // size change. It used to be decorated a tick AFTER it was inserted, so the
  // plain Dawn pills painted for a frame (the flash) and the new node replayed
  // the ovRise entrance animation. The incoming node is now decorated before
  // Dawn inserts it (product-info's pre-process callbacks), and the entrance
  // animation is switched off once the page has settled.
  function decorateLabels(scope, data) {
    window.ovelleMoneyFormat = data.money_format || window.ovelleMoneyFormat;
    const meta = data.meta || {};
    scope.querySelectorAll('.product-form__input--pill input[type=radio]').forEach((input) => {
      const label = scope.querySelector(`label[for="${input.id}"]`);
      if (!label || label.dataset.ovDecorated) return;
      label.dataset.ovDecorated = '1';
      const value = input.value;
      const variant = data.variants.find((v) => v.option1 === value);
      const note = meta[value];
      const isSet = data.option_name === 'Set' || data.option_name === 'Bundle' || data.option_name === 'Kit';
      label.classList.add('ov-pill');
      if (isSet) label.classList.add('ov-pill--card');
      const doc = label.ownerDocument || document;
      const hidden = [...label.querySelectorAll('.visually-hidden')].map((n) => n.outerHTML).join('');
      const firstText = [...label.childNodes].filter((n) => n.nodeType === 3).map((n) => n.textContent).join('').trim() || label.textContent.trim();
      const title = doc.createElement('span'); title.className = 'ov-pill__title'; title.textContent = firstText;
      label.innerHTML = hidden;
      label.appendChild(title);
      if (note) { const n = doc.createElement('span'); n.className = 'ov-pill__note'; n.textContent = note; label.appendChild(n); }
      if (variant && data.variants.length > 1) {
        const p = doc.createElement('span'); p.className = 'ov-pill__price';
        p.innerHTML = (variant.compare > variant.price ? `<s>${fmt(variant.compare)}</s> ` : '') + fmt(variant.price);
        label.appendChild(p);
        if (variant.compare > variant.price) {
          const b = doc.createElement('span'); b.className = 'ov-pill__badge'; b.textContent = `Save ${fmt(variant.compare - variant.price)}`; label.appendChild(b);
        } else if (data.popular && value === data.popular) {
          const b = doc.createElement('span'); b.className = 'ov-pill__badge ov-pill__badge--muted'; b.textContent = 'Most popular'; label.appendChild(b);
        }
      }
      if (variant && !variant.available) label.classList.add('ov-pill--soldout');
      if (/trade/i.test(firstText) && !document.body.classList.contains('ov-trade-customer')) { label.classList.add('ov-pill--trade'); }
    });
    // colour swatches (ovelle-color.js adds the same classes, 60 ms later)
    scope.querySelectorAll('.product-form__input--pill, .product-form__input--swatch').forEach((fs) => {
      const legend = fs.querySelector('legend');
      if (!/colou?r/i.test((legend && legend.textContent) || '')) return;
      fs.querySelectorAll('label').forEach((l) => {
        const m = /^(black|white)\b/i.exec(((l.querySelector('.ov-pill__title') || l).textContent || '').trim());
        if (m) l.classList.add('ov-swatch', `ov-swatch--${m[1].toLowerCase()}`);
      });
    });
  }

  function metaData(sectionId) {
    const script = (sectionId && document.querySelector(`[data-ov-variant-meta][data-section="${sectionId}"]`)) || document.querySelector('[data-ov-variant-meta]');
    if (!script) return null;
    try { return JSON.parse(script.textContent); } catch (e) { return null; }
  }

  function decorateVariants(root) {
    root.querySelectorAll('[data-ov-variant-meta]').forEach((script) => {
      let data; try { data = JSON.parse(script.textContent); } catch (e) { return; }
      const sectionId = script.dataset.section;
      const section = document.getElementById(`shopify-section-${sectionId}`) || document;
      decorateLabels(section, data);
    });
  }

  function hookProductInfo() {
    document.querySelectorAll('product-info').forEach((info) => {
      if (info.dataset.ovPre || typeof info.addPreProcessCallback !== 'function') return;
      info.dataset.ovPre = '1';
      info.addPreProcessCallback((incoming) => {
        if (!incoming || !incoming.querySelectorAll) return;
        const data = metaData(info.dataset.section);
        if (data) decorateLabels(incoming, data);
        // a replacement node must not replay the page-load entrance
        if (incoming.matches && incoming.matches('product-info')) {
          incoming.querySelectorAll('.product__info-container').forEach((c) => c.classList.add('ov-entered'));
        }
      });
    });
  }

  function settleEntrance() {
    document.querySelectorAll('.product__info-container').forEach((c) => c.classList.add('ov-entered'));
  }
  (function () {
    const st = document.createElement('style');
    st.textContent = 'html.ov-js:not(.shopify-design-mode) .product__info-container.ov-entered > *{animation:none}';
    document.head.appendChild(st);
  })();
  setTimeout(settleEntrance, 1800);
  document.addEventListener('change', (e) => { if (e.target && e.target.closest && e.target.closest('variant-selects')) settleEntrance(); }, true);

  decorateVariants(document);
  if (window.customElements && customElements.whenDefined) customElements.whenDefined('product-info').then(hookProductInfo);
  document.addEventListener('shopify:section:load', (e) => { decorateVariants(e.target); hookProductInfo(); });
  // safety net: anything that still arrives undecorated gets done on the next tick
  if (window.subscribe && window.PUB_SUB_EVENTS) subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(() => decorateVariants(document), 0));
  else document.addEventListener('DOMContentLoaded', () => { if (window.subscribe) subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(() => decorateVariants(document), 0)); });

  /* ---------------- Add-ons ---------------- */
  class OvelleAddons extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.form);
      this.checks = this.querySelectorAll('.ov-addon__check');
      this.total = this.querySelector('[data-ov-addons-total]');
      this.checks.forEach((c) => c.addEventListener('change', () => this.updateTotal()));
      if (this.form && !this.form.dataset.ovAddons) {
        this.form.dataset.ovAddons = '1';
        this.form.addEventListener('submit', (evt) => this.onSubmit(evt), true);
      }
    }
    selected() { return [...this.checks].filter((c) => c.checked); }
    updateTotal() {
      const sel = this.selected();
      if (!this.total) return;
      this.total.hidden = sel.length === 0;
      this.querySelector('[data-ov-addons-count]').textContent = sel.length;
      this.querySelector('[data-ov-addons-sum]').textContent = fmt(sel.reduce((a, c) => a + (+c.dataset.price), 0));
      this.querySelectorAll('.ov-addon').forEach((li) => li.classList.toggle('is-selected', li.querySelector('input').checked));
    }
    onSubmit(evt) {
      const sel = this.selected();
      if (!sel.length) return; // let Dawn handle the plain case
      evt.preventDefault(); evt.stopImmediatePropagation();
      const productForm = this.form.closest('product-form');
      const btn = this.form.querySelector('[type="submit"]');
      const fd = new FormData(this.form);
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const main = { id: +fd.get('id'), quantity: parseInt(fd.get('quantity')) || 1 };
      if (fd.get('selling_plan')) main.selling_plan = +fd.get('selling_plan');
      // Carry any line item properties (e.g. the Pre-order ship date) onto the main line
      const props = {};
      for (const [k, v] of fd.entries()) {
        const m = /^properties\[(.+)\]$/.exec(k);
        if (m && v) props[m[1]] = v;
      }
      if (Object.keys(props).length) main.properties = props;
      const items = [main].concat(sel.map((c) => ({ id: +c.value, quantity: 1 })));
      const body = { items };
      if (cart) { body.sections = cart.getSectionsToRender().map((s) => s.id); body.sections_url = window.location.pathname; cart.setActiveElement(document.activeElement); }
      if (btn) { btn.setAttribute('aria-disabled', 'true'); btn.classList.add('loading'); btn.querySelector('.loading__spinner')?.classList.remove('hidden'); }
      fetch(window.routes.cart_add_url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(body) })
        .then((r) => r.json())
        .then((res) => {
          if (res.status) { productForm?.handleErrorMessage?.(res.description || res.message); return; }
          if (!cart) { window.location = window.routes.cart_url; return; }
          if (window.publish && window.PUB_SUB_EVENTS) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: main.id, cartData: res });
          cart.renderContents(res);
          sel.forEach((c) => { c.checked = false; }); this.updateTotal();
        })
        .catch((e) => console.error(e))
        .finally(() => { if (btn) { btn.classList.remove('loading'); btn.removeAttribute('aria-disabled'); btn.querySelector('.loading__spinner')?.classList.add('hidden'); } if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty'); });
    }
  }
  if (!customElements.get('ovelle-addons')) customElements.define('ovelle-addons', OvelleAddons);

  /* ---------------- Sticky add-to-bag bar ---------------- */
  function initSticky() {
    const productForm = document.querySelector('.product product-form form');
    const submit = document.querySelector('.product .product-form__submit');
    const title = document.querySelector('.product__title h1, .product__title .h1');
    if (!productForm || !submit || !title || document.querySelector('.ov-sticky')) return;
    const img = document.querySelector('.product__media img');
    // Mirror whatever the real button says — "Add to bag", "Pre-order", "Sold out"…
    const ctaLabel = (submit.dataset.ovStickyLabel || submit.querySelector('span')?.textContent || 'Add to bag').trim();
    const preNote = (submit.dataset.ovPreorderNote || '').trim();
    const bar = document.createElement('div');
    bar.className = 'ov-sticky';
    bar.innerHTML = `<div class="page-width ov-sticky__inner">
        ${img ? `<img src="${img.currentSrc || img.src}" alt="" class="ov-sticky__img">` : ''}
        <div class="ov-sticky__text"><span class="ov-sticky__title">${title.textContent.trim()}</span><span class="ov-sticky__price" data-ov-sticky-price></span>${preNote ? '<span class="ov-sticky__note" data-ov-sticky-note></span>' : ''}</div>
        <button type="button" class="button button--primary ov-sticky__btn" data-ov-sticky-cta></button>
      </div>`;
    document.body.appendChild(bar);
    bar.querySelector('[data-ov-sticky-cta]').textContent = ctaLabel;
    if (preNote) bar.querySelector('[data-ov-sticky-note]').textContent = preNote;
    const priceSource = () => document.querySelector('.product .price__regular .price-item--regular, .product .price__sale .price-item--sale');
    const syncPrice = () => {
      const t = bar.querySelector('[data-ov-sticky-price]');
      const po = document.querySelector('ovelle-purchase-options');
      const plan = po && typeof po.selectedPlan === 'function' ? po.selectedPlan() : null;
      if (t && plan) { t.textContent = fmt(plan.price) + ' / delivery'; return; }
      const p = priceSource(); if (p && t) t.textContent = p.textContent.trim();
    };
    syncPrice();
    document.addEventListener('ovelle:purchase-change', (e) => { const t = bar.querySelector('[data-ov-sticky-price]'); if (e.detail.plan && t) t.textContent = fmt(e.detail.plan.price) + ' / delivery'; else syncPrice(); });
    if (window.subscribe && window.PUB_SUB_EVENTS) subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(syncPrice, 50));
    bar.querySelector('.ov-sticky__btn').addEventListener('click', () => { if (typeof productForm.requestSubmit === 'function') productForm.requestSubmit(submit); else submit.click(); });
    const io = new IntersectionObserver((entries) => { entries.forEach((en) => bar.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0)); }, { threshold: 0 });
    io.observe(submit);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initSticky); else initSticky();
})();
